import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Label from '../ui/Label/FilterLabel';
import { Button, UploaderLayout } from '../ui';
import { IconCircledPlus } from '../ui/Icons';
import { ALLOWED_FILE_TYPES, ATTACHMENT_MAX_SIZES } from '../../consts/consts';
import { selectUploadsTempContainer } from '../../redux/fileUpload/selectors';
import { clearUploadsTempContainer } from '../../redux/fileUpload/slice';
import { UploadDocumentsModal } from './UploadDocumentsModal';
import FileList from './FileList';

const { UploaderWrapper, UploaderHeader, Description, Title, FlexWrapper, ButtonWrapper, ButtonWrapperMobile } =
  UploaderLayout;

/**
 * A component that manages file uploads and displays the current status of the uploaded files.
 * It allows users to add and delete files, and shows a modal for uploading documents.
 *
 * @param {Object} props - Component props
 * @param {string} props.fileContainerId - Identifier for the file container
 * @param {Array} props.files - List of files
 * @param {boolean} props.disabled - Flag to disable the uploader
 * @param {boolean} props.onlyUploadDisabled - If true, only the upload functionality is disabled
 * @param {boolean} props.viewOnly - Flag to hide the upload and delete functionality
 * @param {Object} props.uiSchema - We need to provide this as `false` to disable the upload button when needed. Leftover from old implementation. Must be replaced.
 * @param {Object} props.schema - Schema for the uploader
 * @param {string} props.description - Description of the uploader
 * @param {string} props.title - Title of the uploader section
 * @param {number} props.maxFiles - Maximum number of files allowed
 * @param {Function} props.onUploadFiles - Function to handle file upload
 * @param {Function} props.onDeleteFiles - Function to handle file deletion
 * @param {Array} props.uploadsTempContainer - Temporary container for uploads
 * @param {boolean} props.iconOnlyButtons - If true, buttons show only icons
 */
const UploaderComponent = ({
  activeFileId,
  clearUploadsTempContainer,
  disabled = false,
  fileContainerId = '',
  files,
  hideDescriptionAfterUpload,
  iconOnlyButtons = false,
  onDeleteFiles,
  onlyUploadDisabled = false,
  onUploadFiles,
  requireDeleteConfirmation = true,
  schema = {
    allowedFileTypes: ALLOWED_FILE_TYPES.default,
    description: '',
    maxFiles: 1,
    maxFileSize: ATTACHMENT_MAX_SIZES.default,
    title: 'Datei hinzufügen'
  },
  uiSchema = {},
  uploadsTempContainer,
  viewOnly
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  useEffect(() => {
    let _disabled = disabled || (uiSchema && uiSchema.disabled) || false;
    if (maxNumberOfFiles <= 0) {
      _disabled = true;
    }
    if (disabled || onlyUploadDisabled) {
      _disabled = true;
    }
    setIsButtonDisabled(_disabled);
  }, [disabled, onlyUploadDisabled, uiSchema]);

  const handleOnUploadComplete = () => {
    const newFiles = [];
    uploadsTempContainer.forEach((upload) => {
      const fileExists = files.find((file) => file.id === upload.id);
      if (!fileExists) {
        newFiles.push(upload);
      }
    });
    onUploadFiles(newFiles);
    clearUploadsTempContainer();
  };

  const handleOnDeleteFile = (file) => {
    onDeleteFiles(file.id);
  };

  const handleToggleModal = () => {
    const next = !isModalOpen;
    setIsModalOpen(next);
    if (!next) {
      clearUploadsTempContainer();
    }
  };

  const { allowedFileTypes, description, maxFiles, maxFileSize, title } = schema;
  const maxNumberOfFiles = maxFiles - files?.length;
  const amountOfFiles = files?.length || 0;
  const formattedLabel = `${amountOfFiles} / ${maxFiles}`;
  const uploadButtonTitle = 'Hinzufügen';

  const renderedDescription = () => {
    if (hideDescriptionAfterUpload && files?.length > 0) {
      return null;
    }
    return <Description>{description}</Description>;
  };

  return (
    <UploaderWrapper>
      <UploaderHeader>
        <FlexWrapper>
          <Title>{title}</Title>
          <Label>{formattedLabel}</Label>
        </FlexWrapper>
        {!viewOnly && (
          <ButtonWrapper>
            <Button onClick={handleToggleModal} disabled={isButtonDisabled} type="link" icon={<IconCircledPlus />}>
              {uploadButtonTitle}
            </Button>
          </ButtonWrapper>
        )}
        <UploadDocumentsModal
          isModalOpen={isModalOpen}
          fileContainerId={fileContainerId}
          handleToggleModal={handleToggleModal}
          maxNumberOfFiles={maxNumberOfFiles}
          onUploadComplete={handleOnUploadComplete}
          title={title}
          description={description}
          allowedFileTypes={allowedFileTypes}
          maxFileSize={maxFileSize}
        />
      </UploaderHeader>

      {renderedDescription()}

      {files && !!files.length && (
        <FileList
          files={files}
          onDeleteFile={handleOnDeleteFile}
          disabled={disabled}
          viewOnly={viewOnly}
          onlyUploadDisabled={onlyUploadDisabled}
          iconOnlyButtons={iconOnlyButtons}
          activeFileId={activeFileId}
          requireDeleteConfirmation={requireDeleteConfirmation}
        />
      )}

      {!viewOnly && (
        <ButtonWrapperMobile>
          <Button onClick={handleToggleModal} disabled={isButtonDisabled} type="link" icon={<IconCircledPlus />}>
            {uploadButtonTitle}
          </Button>
        </ButtonWrapperMobile>
      )}
    </UploaderWrapper>
  );
};

UploaderComponent.displayName = 'Uploader';

const mapStateToProps = (state) => ({
  uploadsTempContainer: selectUploadsTempContainer(state)
});

const mapDispatchToProps = {
  clearUploadsTempContainer
};

export const Uploader = connect(mapStateToProps, mapDispatchToProps)(UploaderComponent);
